/*
 * HVACloud AI · Chart-Preset — Task modal.
 *
 * POST /api/plants/{plantId}/chart-presets/ai-suggest → LLM proposal with
 * name/rangeKey/aggregation/series. Operator picks which devices are in
 * scope, writes a free-text prompt, reviews the returned series list, then
 * "Übernehmen" pushes the accepted subset into the current chart page via
 * an in-memory event (see AiChartPresetLauncher).
 *
 * Uses the shared AiTaskChrome so it looks + feels identical to the Schema/
 * Units/Dashboard AI modals: cloud animation, radial progress ring, rotating
 * status messages, Esc-to-close, backdrop-click-to-close-when-idle.
 */

import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { api, ApiError } from '../../api/client';
import { useAsync } from '../../hooks/useAsync';
import type {
  AiSuggestChartPresetResponse,
  AiSuggestSeries,
  PlantSchemaResponse,
} from '../../api/types';
import { AiTaskChrome, type AiTaskStage } from '../ai/AiTaskChrome';
import { AiChartPreset } from '../../icons/HvacIcons';
import { BRAND } from '../../brand/brand';

export interface AiPresetApplyPayload {
  suggestion: AiSuggestChartPresetResponse;
  /** Subset of `suggestion.series` the operator accepted after review. */
  acceptedSeries: AiSuggestSeries[];
}

interface Props {
  plantId: string;
  onClose(): void;
  onApply(payload: AiPresetApplyPayload): void;
}

const PROGRESS_MESSAGES = [
  `${BRAND.aiName} durchsucht die Datenpunkte…`,
  'Passende Serien werden ausgewählt (Temperaturen, Sollwerte, …)',
  'Achsen + Aggregation werden abgestimmt…',
  'Fast fertig — Vorschlag wird zusammengestellt…',
];

const EXAMPLES = [
  'Vorlauf- und Rücklauftemperaturen der Wärmepumpe letzten 7 Tage',
  'Kompressor-Modus + Sollwert + Ist-Temperatur der letzten 24 Stunden',
  'Energieverbrauch je Woche + COP',
  'Alarme + zugehörige Fühlerwerte der letzten Stunde',
];

export function AiPresetModal({ plantId, onClose, onApply }: Props) {
  const [hint, setHint] = useState('');
  const [stage, setStage] = useState<AiTaskStage>('idle');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [result, setResult] = useState<AiSuggestChartPresetResponse | null>(null);
  const [rejected, setRejected] = useState<Set<string>>(new Set());
  /** Empty set = "alle Devices" — Wenn der User keinen Filter setzt, schickt
   *  der Request kein `deviceIds`-Feld und das Backend nimmt alles. */
  const [pickedDeviceIds, setPickedDeviceIds] = useState<Set<string>>(new Set());

  const abortRef = useRef<AbortController | null>(null);
  useEffect(() => () => abortRef.current?.abort(), []);

  const plantState = useAsync(
    (signal) => api.get<PlantSchemaResponse>(`/api/plants/${plantId}/schema`, signal),
    [plantId],
  );
  const devices = plantState.status === 'ok' ? plantState.data.devices : [];

  const scopeLabel = useMemo(() => {
    if (pickedDeviceIds.size === 0) return `alle ${devices.length} Devices`;
    if (pickedDeviceIds.size === 1) {
      const only = devices.find((d) => pickedDeviceIds.has(d.id));
      return only ? `nur ${only.name}` : '1 Device';
    }
    return `${pickedDeviceIds.size} Devices`;
  }, [pickedDeviceIds, devices]);

  const toggleDevice = (id: string) => {
    setPickedDeviceIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const clearDeviceFilter = () => setPickedDeviceIds(new Set());

  const run = async (freeText?: string) => {
    const text = (freeText ?? hint).trim();
    if (text.length === 0) return;

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setStage('loading');
    setErrorMsg(null);
    setResult(null);
    setRejected(new Set());
    try {
      const body: { prompt: string; deviceIds?: string[] } = { prompt: text };
      if (pickedDeviceIds.size > 0) body.deviceIds = Array.from(pickedDeviceIds);
      const res = await api.post<AiSuggestChartPresetResponse>(
        `/api/plants/${plantId}/chart-presets/ai-suggest`,
        body,
        controller.signal,
      );
      if (controller.signal.aborted) return;
      setResult(res);
      setStage('result');
    } catch (err) {
      if (controller.signal.aborted) return;
      setErrorMsg(explain(err));
      setStage('error');
    }
  };

  const acceptedSeries = result
    ? result.series.filter((s) => !rejected.has(s.leafId))
    : [];

  const apply = () => {
    if (!result) return;
    onApply({ suggestion: result, acceptedSeries });
  };

  // Wenn der User "Neu vorschlagen" waehlt, brechen wir die aktuelle Anfrage
  // ab und starten mit dem noch aktiven Hint erneut — der Hint bleibt stehen,
  // damit man nachfeilen kann statt komplett neu zu tippen.
  const closeAndCancel = () => {
    abortRef.current?.abort();
    onClose();
  };

  const busy = stage === 'loading';

  return (
    <AiTaskChrome
      title={`${BRAND.aiName} · Auswertung vorschlagen`}
      hint={hint}
      onHintChange={setHint}
      hintPlaceholder="z.B. Vorlauf-/Rücklauftemperaturen der letzten 7 Tage"
      stage={stage}
      errorMessage={errorMsg}
      progressMessages={PROGRESS_MESSAGES}
      onClose={closeAndCancel}
      hero={<AiChartPreset size={72} style={{ color: 'var(--ai-accent)' }} />}
      footer={
        stage === 'result' ? (
          <>
            <span class="muted" style={{ marginRight: 'auto' }}>
              {acceptedSeries.length}/{result?.series.length ?? 0} Serien angenommen
            </span>
            <button type="button" class="hv-btn" onClick={() => { setResult(null); setStage('idle'); }}>
              Neu vorschlagen
            </button>
            <button type="button" class="hv-btn hv-btn--primary"
                    onClick={apply}
                    disabled={acceptedSeries.length === 0}>
              Übernehmen
            </button>
          </>
        ) : (
          <>
            <div style={{ flex: 1 }} />
            <button type="button" class="hv-btn" onClick={closeAndCancel} disabled={busy}>
              Abbrechen
            </button>
            <button type="button" class="hv-btn hv-btn--primary" onClick={() => void run()}
                    disabled={busy || hint.trim().length === 0 || plantState.status !== 'ok'}>
              {busy ? `${BRAND.aiName} arbeitet…` : 'Vorschlagen'}
            </button>
          </>
        )
      }
    >
      {stage === 'result' && result && (
        <>
          <div class="hv-modal__field" style={{ marginTop: 0 }}>
            <label>Vorgeschlagenes Preset</label>
            <div style={{ fontWeight: 600, fontSize: '1.05em' }}>{result.name}</div>
            <div class="muted" style={{ fontSize: '0.9em' }}>
              {result.rangeKey} · {result.aggregation} · {acceptedSeries.length}/{result.series.length} Serien · Scope: {scopeLabel}
            </div>
          </div>

          {result.rationale && (
            <p class="hv-ai-modal__rationale">{result.rationale}</p>
          )}

          {result.droppedLeafIds.length > 0 && (
            <p class="muted" style={{ marginTop: 0 }}>
              KI schlug {result.droppedLeafIds.length} unbekannte
              Datenpunkt{result.droppedLeafIds.length === 1 ? '' : 'e'} vor — wurden entfernt.
            </p>
          )}

          <h4>Vorgeschlagene Serien</h4>
          {result.series.length === 0 ? (
            <p class="muted" style={{ marginTop: 0 }}>Keine passenden Datenpunkte gefunden.</p>
          ) : (
            <table class="hv-data-table">
              <thead>
                <tr>
                  <th style={{ width: '2em' }}></th>
                  <th>Leaf</th>
                  <th style={{ width: '6em' }}>Typ</th>
                  <th style={{ width: '6em' }}>Stil</th>
                  <th>Achse</th>
                </tr>
              </thead>
              <tbody>
                {result.series.map((s) => {
                  const isRejected = rejected.has(s.leafId);
                  return (
                    <tr key={s.leafId} style={isRejected ? { opacity: 0.5 } : undefined}>
                      <td>
                        <input
                          type="checkbox"
                          checked={!isRejected}
                          onChange={(e) => {
                            const checked = (e.currentTarget as HTMLInputElement).checked;
                            setRejected((prev) => {
                              const next = new Set(prev);
                              if (checked) next.delete(s.leafId);
                              else next.add(s.leafId);
                              return next;
                            });
                          }}
                        />
                      </td>
                      <td class="mono" style={{ fontSize: '0.9em' }}>{s.leafId.slice(0, 8)}…</td>
                      <td>{s.renderType}</td>
                      <td>{s.lineStyle ?? 'solid'}</td>
                      <td class="muted">{s.axisGroup ?? '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </>
      )}

      {(stage === 'idle' || stage === 'error') && (
        <>
          <div class="hv-modal__field">
            <label>
              Devices im Scope
              <span class="muted" style={{ marginLeft: '0.5em', fontWeight: 'normal' }}>
                ({scopeLabel})
              </span>
            </label>
            {plantState.status === 'loading' && (
              <p class="muted" style={{ marginTop: 0 }}>Devices werden geladen…</p>
            )}
            {plantState.status === 'error' && (
              <p class="muted" style={{ marginTop: 0 }}>
                Devices konnten nicht geladen werden — KI verwendet alle Datenpunkte des Plants.
              </p>
            )}
            {plantState.status === 'ok' && devices.length === 0 && (
              <p class="muted" style={{ marginTop: 0 }}>
                Keine Devices auf diesem Plant.
              </p>
            )}
            {plantState.status === 'ok' && devices.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4em', marginTop: '0.3em' }}>
                {devices.map((d) => {
                  const on = pickedDeviceIds.has(d.id);
                  return (
                    <button
                      key={d.id}
                      type="button"
                      class={on ? 'hv-chip hv-chip--on' : 'hv-chip'}
                      onClick={() => toggleDevice(d.id)}
                      title={d.symbol}
                    >
                      {d.name}
                    </button>
                  );
                })}
                {pickedDeviceIds.size > 0 && (
                  <button
                    type="button"
                    class="hv-chip hv-chip--clear"
                    onClick={clearDeviceFilter}
                    title="Filter zurücksetzen — alle Devices"
                  >
                    × zurücksetzen
                  </button>
                )}
              </div>
            )}
          </div>

          <div class="hv-modal__field">
            <label>Beispiele</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4em' }}>
              {EXAMPLES.map((ex) => (
                <button
                  key={ex}
                  type="button"
                  class="hv-chip"
                  onClick={() => { setHint(ex); void run(ex); }}
                  disabled={busy}
                >
                  {ex}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </AiTaskChrome>
  );
}

function explain(e: unknown): string {
  if (e instanceof ApiError) {
    const body = e.body as { error?: string; detail?: string; title?: string } | undefined;
    return body?.error ?? body?.detail ?? body?.title ?? `Fehler ${e.status}`;
  }
  if (e instanceof Error) return e.message;
  return 'Unbekannter Fehler.';
}
